import * as React from 'react';
import { userSession } from 'state/session';
import { energyLabelState } from 'state/energyLabel';
import { tenantConfig } from 'utils/tenant';
import { resolveLabelUrl, invalidateCacheEntry } from 'utils/energyLabel/resolver';
import { handleResolverFallback } from 'utils/energyLabel/fallback';
import { isTrustedEnergyLabelUrl } from 'utils/energyLabel/trustedLabelUrl';
import { isEnergyLabelSuppressed } from 'utils/energyLabel/marketRules';
import { validateImageDimensions } from 'utils/energyLabel/validateImage';
import { getLocale } from 'i18n/locale';
import { getCountryISO2 } from 'utils/isoConverter';
import type { IBrand, IMarket, IVehicle } from 'data/schema';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 8000];

interface IResolvedFor {
  vehicleId: string;
  efficiencyClass: string | undefined;
  market: string;
  brand: IBrand;
}

export function useEnergyLabelUrl(vehicleId: string | undefined): string | null {
  const session = userSession().useState();
  const labelState = energyLabelState().useState();
  tenantConfig().useState();

  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(null);
  const resolvedForRef = React.useRef<IResolvedFor | undefined>(undefined);
  const [retryCount, setRetryCount] = React.useState(0);

  const market = session.market ?? '';
  const brand = (session.brand ?? '') as IBrand;
  const isGated = tenantConfig().isAllowed('energyLabel');

  const integratorUrl = vehicleId ? (labelState.integratorUrls[vehicleId] ?? null) : null;

  const vehicle = vehicleId ? (session.vehicles?.[vehicleId] as IVehicle | undefined) : undefined;
  const efficiencyClass = vehicle?.efficiencyClass;

  // Some markets withhold the label for used stock entirely. Deliberately evaluated *after*
  // integratorUrl and applied only to the two sources below it: an integrator URL is the embedding
  // partner's own asset and keeps precedence, whereas the rule exists because our used-car data
  // cannot be trusted. Suppression is also why the resolver must not run — a withheld label is an
  // expected outcome, not a resolution failure, so it must raise no dealer warning.
  const isSuppressed = isEnergyLabelSuppressed(market as IMarket, vehicle?.isUsedCar);

  // The lambda hands us a complete label URL — from VTP for new cars, otherwise derived — carrying
  // segments the class-only resolver below cannot express (France needs a trailing CO2 figure).
  // When present it short-circuits the resolver exactly as an integrator URL does.
  // It arrives from an external API and is persisted on the session, so the host is verified before
  // use; an untrusted value falls through to the class-derived resolver instead.
  const candidateVtpUrl =
    !isSuppressed && isTrustedEnergyLabelUrl(vehicle?.energyLabelUrl) ? vehicle!.energyLabelUrl! : null;

  // Unlike an integrator URL, which the embedding partner supplies contractually, this one comes
  // from a third-party API and may 404 or serve a placeholder. Verify it the same way the resolver
  // verifies its own result, and drop it on failure so the class-derived chain — including the
  // not-available fallback — still runs instead of leaving a broken image on screen.
  const [rejectedVtpUrl, setRejectedVtpUrl] = React.useState<string | null>(null);
  const vtpLabelUrl = candidateVtpUrl && candidateVtpUrl !== rejectedVtpUrl ? candidateVtpUrl : null;
  const directUrl = integratorUrl ?? vtpLabelUrl;

  React.useEffect(() => {
    if (!isGated || !candidateVtpUrl || candidateVtpUrl === rejectedVtpUrl) return;

    let cancelled = false;
    validateImageDimensions(candidateVtpUrl).then((isValid) => {
      if (!cancelled && !isValid) setRejectedVtpUrl(candidateVtpUrl);
    });

    return () => {
      cancelled = true;
    };
  }, [isGated, candidateVtpUrl, rejectedVtpUrl]);

  React.useEffect(() => {
    if (!isGated || !vehicleId || directUrl || isSuppressed) {
      setResolvedUrl(null);
      resolvedForRef.current = undefined;
      setRetryCount(0);
      return;
    }

    const ref = resolvedForRef.current;
    if (
      ref?.vehicleId === vehicleId &&
      ref?.efficiencyClass === efficiencyClass &&
      ref?.market === market &&
      ref?.brand === brand &&
      retryCount === 0
    ) {
      return;
    }

    let cancelled = false;
    const country = getCountryISO2(market) ?? '';
    const locale = getLocale(market as IMarket);
    const lang = locale.split('_')[0].toLowerCase();
    const energyLocale = country && lang ? `${country}-${lang}` : '';

    const resolve = () => {
      resolveLabelUrl(brand, { efficiencyClass, locale: energyLocale }).then(async (url) => {
        if (cancelled) return;

        if (!url) {
          setResolvedUrl(null);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
          handleResolverFallback();
          return;
        }

        const isValid = await validateImageDimensions(url);
        if (cancelled) return;

        if (isValid) {
          setResolvedUrl(url);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
          return;
        }

        if (retryCount < MAX_RETRIES) {
          if (efficiencyClass) {
            invalidateCacheEntry(efficiencyClass, energyLocale);
          }
          const delay = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
          setTimeout(() => {
            if (!cancelled) {
              setRetryCount((prev) => prev + 1);
            }
          }, delay);
        } else {
          setResolvedUrl(url);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
        }
      });
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [isGated, vehicleId, directUrl, isSuppressed, brand, market, efficiencyClass, retryCount]);

  if (!isGated) return null;
  return directUrl ?? resolvedUrl;
}
